import type { Game } from '../core/game';
import { ARTISAN_NODE, ARTISAN_TOAST, abyssOpenedOf, abyssOpenedText, nodeNameOf, onGameEventWith } from './abyssApi';
import { toast } from './toast';

/**
 * Avis du mode infini (A94, v0.46.0), posés une fois pour toute l'application :
 *  - événement `abyss` : « L'Abîme s'ouvre : Matériau » — **un toast par nœud**
 *    (« Grimoire des nombres · niveau 2 » pour un niveau suivant) ;
 *  - événement `purchase` de l'Artisan des dés (acheté ou offert à l'entrée en
 *    mode infini) : « Artisan des dés : la Forge et les Corrupted GachaDice
 *    sont ouvertes ».
 *
 * `toast()` n'affiche qu'un message à la fois (le nouveau remplace l'ancien) :
 * les messages arrivés ensemble — plusieurs portes franchies d'un coup — passent
 * donc **l'un après l'autre**, chacun laissé lisible avant le suivant.
 */

/** Durée d'affichage d'un avis de l'Abîme (ms) ; le suivant prend sa place ensuite. */
export const ABYSS_TOAST_MS = 2400;

export function mountAbyssToasts(game: Game): () => void {
  const queue: string[] = [];
  let timer: number | undefined;

  const pump = (): void => {
    const next = queue.shift();
    if (next === undefined) {
      timer = undefined;
      return;
    }
    toast(next, ABYSS_TOAST_MS, 'node');
    timer = window.setTimeout(pump, ABYSS_TOAST_MS + 100);
  };
  const push = (messages: string[]): void => {
    if (messages.length === 0) return;
    queue.push(...messages);
    if (timer === undefined) pump();
  };

  const offAbyss = onGameEventWith(game, 'abyss', (payload) => {
    push(abyssOpenedOf(payload).map((o) => abyssOpenedText(o, nodeNameOf(o.nodeId))));
  });
  const offPurchase = onGameEventWith(game, 'purchase', (payload) => {
    const nodeId = (payload as { nodeId?: unknown } | null)?.nodeId;
    if (nodeId === ARTISAN_NODE) push([ARTISAN_TOAST]);
  });

  return () => {
    offAbyss();
    offPurchase();
    queue.length = 0;
    if (timer !== undefined) window.clearTimeout(timer);
    timer = undefined;
  };
}
